/** Wire shapes for the terminal-payment REST + hub surface. Mirrors the backend Contract. */

export type TerminalPaymentOutcome = 'Paid' | 'Aborted' | 'Failed' | 'TimedOut';

/** Side-effect-free quote from `terminal-open-fee`. */
export interface TerminalOpenFee {
  hasOpenFee: boolean;
  alreadyPaid: boolean;
  amountInCents: number | null;
  currency: string | null;
  reason: string | null;
  /** Server instant this fee was priced against; echoed back on start so the charge uses the same instant. */
  pricedAt: string | null;
  /** Nothing due because a parking right covers the stay — shown as "covered", never as "paid". */
  isCovered: boolean;
  /** The quoted stay, in the parker's terms. Null when no stay was found. */
  stay: QuotedStay | null;
  /**
   * Parker credit usable for this stay here: min(balance, amount due), 0 when there is none or the
   * wallet does not allow this site. Only offered — spent only when the parker taps "Mit Guthaben".
   */
  redeemableCreditInCents: number;
}

/** The station's coin acceptor as the kiosk needs it. Coins are offered only when enabled AND online. */
export interface StationCoinAcceptor {
  enabled: boolean;
  online: boolean;
  acceptedCoinValuesInCents: number[];
}

/** How a coin session ended (mirrors the backend's CoinSessionEndReason). */
export type CoinEndReason =
  | 'TargetReached'
  | 'SwitchToCard'
  | 'Cancel'
  | 'InactivityTimeout'
  | 'Fault'
  | 'RecoveredAfterRestart';

export interface CoinPaymentStarted {
  paymentRequestId: string;
  amountInCents: number;
  currency: string;
  acceptedCoinValuesInCents: number[];
}

/** `CoinProgress` hub push — display only, one per coin. Money is booked from the Pi's signed outcome. */
export interface CoinProgressMessage {
  paymentRequestId: string;
  coinValueInCents: number;
  collectedInCents: number;
  targetInCents: number;
}

export interface WalletRedemption {
  redeemedInCents: number;
  remainingDueInCents: number;
  covered: boolean;
}

/** Which stay a quote is about: entry, exit, zone and space — never an id. */
export interface QuotedStay {
  startedAt: string;
  endedAt: string | null;
  parkingSpaceName: string | null;
  parkingZoneName: string | null;
}

/**
 * Station branding/presentation, from `terminal-station-config`. Clean, non-optional shape mapped from the
 * generated DTO so the UI doesn't deal with the generator's all-optional fields.
 */
export interface StationConfig {
  spaceName: string;
  hotline: string | null;
  countryPrefix: string;
  defaultLanguage: string;
  languages: string[];
  logoUrl: string | null;
  /** Null when the station has no coin acceptor — the usual case. */
  coinAcceptor: StationCoinAcceptor | null;
}

/** Latest ANPR detection for a plate, from `terminal-vehicle-image` — the confirm/correct screen. */
export interface VehicleDetection {
  hasImage: boolean;
  plateNumber: string;
  vehicleImageUrl: string | null;
  plateImageUrl: string | null;
  detectedAt: string | null;
}

/**
 * One candidate on the `notFound` recovery screen — a vehicle currently parked in this station's space whose
 * plate is close to what the parker typed. Sourced from the `terminal-near-matches` endpoint (active stays
 * only), mapped from the backend's raw score/instant into pre-formatted display strings.
 */
export interface NearMatchDetection {
  plate: string;
  /** Pre-formatted match score, e.g. "96.2%". */
  matchScore: string;
  /** Pre-formatted stay-start time, e.g. "17:24". */
  time: string;
  /** The active parking process id — sent to the correction endpoint. */
  parkingProcessId: string;
  /** Latest ANPR full-frame vehicle image, or null. */
  vehicleImageUrl: string | null;
  /** Latest ANPR plate-crop image, or null. */
  plateImageUrl: string | null;
}

export interface TerminalPaymentStarted {
  paymentRequestId: string;
  amountInCents: number;
  currency: string;
}

/** `PaymentDone` hub push — the attempt reached a terminal state. */
export interface PaymentDoneMessage {
  paymentRequestId: string;
  paymentSessionId: string;
  outcome: TerminalPaymentOutcome;
  statusReason?: string | null;
  // Coin sessions only: what the coins did.
  collectedInCents?: number | null;
  appliedInCents?: number | null;
  creditedInCents?: number | null;
  remainingDueInCents?: number | null;
  coinEndReason?: CoinEndReason | null;
}

/**
 * `statusReason` on a `PaymentDone` push meaning the command never reached the reader.
 *
 * Mirrors `PaymentPolicy.TerminalUnreachableStatusReason` on the backend — the two must stay
 * identical. If they drift, this screen silently degrades to "payment failed", which tells someone
 * standing at a working column that their card was refused. It was not: we never reached the reader.
 */
export const TERMINAL_UNREACHABLE = 'TerminalUnreachable';

/** `FiscalReceiptReady` hub push — the EFSTA receipt URL is available. */
export interface FiscalReceiptReadyMessage {
  paymentRequestId: string;
  paymentSessionId: string;
  receiptUrl: string;
}

/** The kiosk's single-screen state machine. */
export type KioskPhase =
  | 'idle'        // branding / standby — tap to wake
  | 'entry'       // typing the plate
  | 'detection'   // ANPR confirm / correct
  | 'notFound'    // plate not found - show similar ANPR matches & OCR fix options
  | 'quoting'     // asking the backend the fee
  | 'confirm'     // showing the amount, waiting for "pay"
  | 'arming'      // POST terminal-start in flight
  | 'tap'         // "please tap your card"
  | 'redeeming'   // spending credit the parker chose to use
  | 'coinsStarting' // coin slot requested; waiting for the station to confirm it opened
  | 'coins'       // "please insert coins" — live total from CoinProgress
  | 'coinsStopping' // parker stopped; waiting for the station to count and report the coins
  | 'coinsCredited' // coins kept as credit (cancelled / walked away) — nothing applied to the stay
  | 'success'     // paid; showing the receipt (or waiting for it)
  | 'alreadyPaid' // nothing to charge — the plate's fee is already settled (benign, not an error)
  | 'unreachable' // the command never reached the reader — not a card problem, and retrying is free
  | 'failed';     // declined / aborted / error
